import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * SessionExpiredDialog — Mobile ARIA Phase 1
 *
 * Listens for the global "aria:session-expired" CustomEvent emitted by
 * lib/api.js when an authenticated request returns 401. Opens a modal
 * that re-authenticates the clinician IN PLACE — no full-page redirect,
 * no loss of the current chairside state.
 *
 * Design principles:
 *  - Only mounts one dialog at a time (idempotent regardless of event
 *    burst — a batch of failed requests still yields ONE prompt).
 *  - Reuses AuthContext.login so 2FA / captcha branches keep working.
 *    If a 2FA challenge is returned we tell the clinician to complete
 *    login on the /login page (rare case; keeps this dialog small).
 *  - No PHI: only email + password fields.
 *  - Fully keyboard-accessible; ESC closes.
 *  - Never shows on the initial cold-boot /auth/me probe (that path
 *    does not have a stored token, so lib/api.js suppresses the event).
 */
export default function SessionExpiredDialog() {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef(null);
  const originRef = useRef(null);

  useEffect(() => {
    const onExpired = (ev) => {
      // Idempotent: ignore bursts while the dialog is already open.
      setOpen((prev) => {
        if (prev) return true;
        originRef.current = (ev && ev.detail && ev.detail.url) || null;
        return true;
      });
      // Pre-fill the email field with the currently-known user so the
      // clinician only has to retype their password. When we no longer
      // have a user object (already cleared) the field stays empty.
      setEmail((prev) => prev || (user && user.email) || "");
      setError("");
      setPassword("");
    };
    if (typeof window !== "undefined") {
      window.addEventListener("aria:session-expired", onExpired);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("aria:session-expired", onExpired);
      }
    };
  }, [user]);

  // Focus the first empty field for one-hand reauth.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (emailRef.current) {
        try { emailRef.current.focus(); } catch (_) { /* ignore */ }
      }
    }, 30);
    return () => clearTimeout(t);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setPassword("");
    setError("");
  }, []);

  // ESC to close (allows the clinician to defer reauth and pick up
  // their session on next request).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Enter your email and password to continue.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await login(email, password);
      if (res && res.requires_2fa) {
        setError(
          "Two-factor step required. Please sign in on the login screen to complete verification.",
        );
        setSubmitting(false);
        return;
      }
      // Success — close dialog. Chairside state was never unmounted, so
      // the clinician resumes exactly where they were.
      setSubmitting(false);
      close();
    } catch (err) {
      setSubmitting(false);
      const msg =
        (err && err.response && err.response.data && err.response.data.detail) ||
        "Sign-in failed. Please try again.";
      setError(String(msg).slice(0, 240));
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="session-expired-backdrop"
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(0,0,0,0.68)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
      onClick={(e) => {
        // Only backdrop clicks close — clicks on the panel bubble up here
        // but we detect via currentTarget.
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        data-testid="session-expired-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        style={{
          width: "min(420px, 100%)",
          background: "var(--pa-bg-2, #111)",
          border: "1px solid rgba(214,176,108,0.4)",
          borderRadius: "12px",
          padding: "20px",
          color: "var(--pa-text, #fff)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        <h2
          id="session-expired-title"
          className="text-base sm:text-lg font-semibold"
          style={{ marginBottom: 6 }}
        >
          Please sign in again
        </h2>
        <p
          className="text-sm"
          style={{ color: "var(--pa-text-muted, #aaa)", marginBottom: 14 }}
        >
          Your session ended. Sign in to resume where you left off — nothing on
          this page has been lost.
        </p>
        <form onSubmit={onSubmit} noValidate>
          <label className="text-xs" style={{ color: "var(--pa-text-muted, #aaa)" }}>
            Email
          </label>
          <input
            ref={emailRef}
            data-testid="session-expired-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full text-sm p-2 mt-1 mb-3 rounded-md"
            style={{
              background: "var(--pa-bg, #0b0b0b)",
              border: "1px solid var(--pa-border, #333)",
              color: "var(--pa-text, #fff)",
            }}
          />
          <label className="text-xs" style={{ color: "var(--pa-text-muted, #aaa)" }}>
            Password
          </label>
          <input
            data-testid="session-expired-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full text-sm p-2 mt-1 rounded-md"
            style={{
              background: "var(--pa-bg, #0b0b0b)",
              border: "1px solid var(--pa-border, #333)",
              color: "var(--pa-text, #fff)",
            }}
          />
          {error && (
            <div
              data-testid="session-expired-error"
              role="alert"
              className="text-xs mt-3"
              style={{ color: "#fca5a5" }}
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              data-testid="session-expired-cancel"
              onClick={close}
              className="text-sm px-3 py-2 rounded-md"
              style={{
                background: "transparent",
                color: "var(--pa-text-muted, #aaa)",
                border: "1px solid var(--pa-border, #333)",
              }}
            >
              Later
            </button>
            <button
              type="submit"
              data-testid="session-expired-submit"
              disabled={submitting || !email || !password}
              className="text-sm font-semibold px-3 py-2 rounded-md"
              style={{
                background: "var(--pa-gold, #d4af37)",
                color: "var(--pa-bg, #0a0a0a)",
                opacity: submitting || !email || !password ? 0.5 : 1,
                cursor: submitting || !email || !password ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
